// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Contractors CRUD
// Company management with rates per debris type
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { DebrisContractor, ContractorStatus } from '../../types/debris';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'completed', label: 'Completed' },
];

const STATUS_COLORS: Record<string, string> = {
  active: 'green', suspended: 'red', completed: 'blue',
};

const EMPTY: Partial<DebrisContractor> = {
  companyName: '', contractNumber: '', contactName: '', contactPhone: '', contactEmail: '',
  status: 'active', notes: '',
};

export default function Contractors() {
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('debris');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    contractors, loadingContractors,
    loadContractors, createContractor, updateContractor, deleteContractor,
  } = useDebrisStore();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DebrisContractor | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DebrisContractor | null>(null);
  const [form, setForm] = useState<Partial<DebrisContractor>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadContractors(); }, [loadContractors]);

  const iContractors = incidentId ? contractors : [];

  const filtered = useMemo(() => {
    let list = iContractors;
    if (statusFilter && statusFilter !== 'all') list = list.filter(c => c.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        (c.companyName || '').toLowerCase().includes(q) ||
        (c.contractNumber || '').toLowerCase().includes(q) ||
        (c.contactName || '').toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''));
  }, [iContractors, statusFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [statusFilter, search]);

  const setField = (key: keyof DebrisContractor, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => { setEditTarget(null); setForm({ ...EMPTY }); setModalOpen(true); };
  const openEdit = (c: DebrisContractor) => { setEditTarget(c); setForm({ ...c }); setModalOpen(true); };

  const handleSave = async () => {
    if (!form.companyName?.trim()) {
      notifications.show({ message: 'Company name is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, incidentId };
      if (editTarget) {
        await updateContractor(editTarget.id, payload);
        notifications.show({ message: 'Contractor updated', color: 'green' });
      } else {
        await createContractor(payload);
        notifications.show({ message: 'Contractor created', color: 'green' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteContractor(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.companyName} deleted`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };


  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Contractors</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingContractors} onClick={() => loadContractors()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Contractor</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search company, contract #, contact..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} contractor{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Company</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Contract #</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Contact</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingContractors ? 'Loading...' : 'No contractors found'}
                </td></tr>
              )}
              {pageRows.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', cursor: 'pointer' }} onClick={() => openEdit(c)}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>{c.companyName || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{c.contractNumber || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <Text size="sm">{c.contactName || '-'}</Text>
                    <Text size="xs" c="dimmed">{c.contactEmail}</Text>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Badge size="sm" color={STATUS_COLORS[c.status] ?? 'gray'}>{c.status}</Badge>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                    <Group gap={4} justify="flex-end">
                      {canEdit && <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(c)}>
                        <IconPencil size={14} />
                      </ActionIcon>}
                      {canEdit && <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(c)}>
                        <IconTrash size={14} />
                      </ActionIcon>}
                    </Group>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {totalPages > 1 && (
        <Group justify="center" mt="md" gap="xs">
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="blue" size="xs" onClick={() => setPage(p)}>{p}</Button>;
          })}
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Contractor' : 'Add Contractor'} centered size="lg">
        <Stack>
          <Text size="sm" fw={600} c="dimmed">Company Info</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Company Name" required value={form.companyName ?? ''}
              onChange={e => setField('companyName', e.target.value)} />
            <TextInput label="Contract Number" value={form.contractNumber ?? ''}
              onChange={e => setField('contractNumber', e.target.value)} />
            <TextInput label="Contact Name" value={form.contactName ?? ''}
              onChange={e => setField('contactName', e.target.value)} />
            <TextInput label="Contact Phone" value={form.contactPhone ?? ''}
              onChange={e => setField('contactPhone', e.target.value)} />
            <TextInput label="Contact Email" value={form.contactEmail ?? ''}
              onChange={e => setField('contactEmail', e.target.value)} />
            <Select label="Status" data={STATUS_OPTIONS}
              value={(form.status as ContractorStatus) ?? 'active'}
              onChange={v => setField('status', v ?? 'active')} />
          </SimpleGrid>

          <Paper p="sm" radius="sm" withBorder style={{ background: 'var(--mantine-color-gray-0)' }}>
            <Text size="sm" c="dimmed">
              Rates are managed in the <Text span fw={600} c="blue" component="a" href="/debris/rates" style={{ cursor: 'pointer' }}>Rate Schedule</Text> page with FEMA-compliant line items and distance tiers.
            </Text>
          </Paper>

          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} autosize minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Contractor'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Contractor" centered size="sm">
        <Stack>
          <Text size="sm">Delete <strong>{deleteTarget?.companyName}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
